import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  TextField,
  Button,
  Box,
  Slider,
  IconButton,
  Chip,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { db, auth } from "../firebase";
import { getDoc, doc, collection, setDoc, serverTimestamp } from "firebase/firestore";
import Atras from "./atras";

const PublishProposal = () => {
  const [formData, setFormData] = useState({
    description: "",
    price: "",
    timeToComplete: "",
  });
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [project, setProject] = useState(null);

  // Get projectId from query parameters
  const queryParams = new URLSearchParams(location.search);
  const projectId = queryParams.get('proyectId');

  useEffect(() => {
    const fetchUserAndProject = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        setUser(currentUser);
      } else {
        console.error("No user is signed in");
        setError("No user is signed in. Please log in to submit a proposal.");
        return;
      }

      if (!projectId) {
        console.error("No project ID provided in the URL");
        setError("No project ID provided. Please try again from the project page.");
        return;
      }

      try {
        const projectDoc = await getDoc(doc(db, "projects", projectId));
        if (projectDoc.exists()) {
          setProject(projectDoc.data());
        } else {
          console.error("Project not found");
          setError("Project not found. Please check the project ID and try again.");
        }
      } catch (error) {
        console.error("Error fetching project:", error);
        setError("An error occurred while fetching the project. Please try again later.");
      }
    };

    fetchUserAndProject();
  }, [projectId]);

  const budget = Number(project?.price) || 0;

  const priceRange = useMemo(() => {
    if (budget <= 0) {
      return { min: 0, max: 1000, step: 10 };
    }
    return {
      min: 0,
      max: Math.max(Math.round(budget * 1.5), budget + 50),
      step: budget >= 500 ? 10 : 5,
    };
  }, [budget]);

  const MAX_DAYS = 30; // tope de un mes para el plazo de una propuesta

  const DEFAULT_DAYS = 7;

  const suggestedDays = useMemo(() => {
    if (!project?.timeToDo?.toDate) return DEFAULT_DAYS;
    const diffMs = project.timeToDo.toDate() - new Date();
    const daysUntilDeadline = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    // Si la fecha de entrega ya pasó o es demasiado próxima para ser un plazo
    // realista, no la usamos: caería en sugerencias como "1 día" para todo.
    if (daysUntilDeadline < 3) return DEFAULT_DAYS;
    return Math.min(MAX_DAYS, daysUntilDeadline);
  }, [project]);

  const daysRange = { min: 1, max: MAX_DAYS, step: 1 };

  // Precarga el precio y el plazo sugeridos apenas se conoce el proyecto.
  useEffect(() => {
    if (budget > 0 && !formData.price) {
      setFormData((prev) => ({ ...prev, price: String(budget) }));
    }
    if (suggestedDays > 0 && !formData.timeToComplete) {
      setFormData((prev) => ({ ...prev, timeToComplete: String(suggestedDays) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budget, suggestedDays]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  const setPrice = (value) => {
    const clamped = Math.max(0, Math.round(value));
    setFormData((prev) => ({ ...prev, price: String(clamped) }));
  };

  const handleSliderChange = (_event, value) => setPrice(value);

  const handleStep = (delta) => setPrice((Number(formData.price) || 0) + delta);

  const setDays = (value) => {
    const clamped = Math.min(MAX_DAYS, Math.max(1, Math.round(value)));
    setFormData((prev) => ({ ...prev, timeToComplete: String(clamped) }));
  };

  const handleDaysSliderChange = (_event, value) => setDays(value);

  const handleDaysStep = (delta) => setDays((Number(formData.timeToComplete) || 1) + delta);

  const handleDaysInputChange = (e) => {
    if (e.target.value === "") {
      setFormData((prev) => ({ ...prev, timeToComplete: "" }));
      return;
    }
    setDays(e.target.value);
  };

  const handleAddProposal = async (e) => {
    e.preventDefault();
    setError(null);

    if (!user || !project) {
      setError("Unable to submit proposal. Please make sure you're logged in and the project exists.");
      return;
    }

    try {
      const projectRef = doc(db, "projects", projectId);
      const proposalRef = doc(collection(projectRef, "proposals"));

      const proposalData = {
        proposalId: proposalRef.id,
        projectId: projectId,
        clientUid: project.uid, // Changed from project.clientUid to project.uid
        freelanceUid: user.uid,
        description: formData.description,
        price: formData.price,
        timeToComplete: formData.timeToComplete,
        status: "pending",
        timestamp: serverTimestamp(),
      };

      await setDoc(proposalRef, proposalData);
      await setDoc(doc(db, "proposals", proposalRef.id), proposalData);

      setPublishSuccess(true);
    } catch (error) {
      console.error("Error adding proposal:", error.message);
      setError("An error occurred while submitting the proposal. Please try again.");
    }
  };

  const handleRedirectHome = () => {
    navigate("/home");
  };

  return (
    <Container>
      <AppBar position="fixed">
        <Toolbar
          sx={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            padding: "10px",
          }}
        >
          <Atras color="inherit" />
          <Container>
            <Typography variant="h5" color="text.primary" gutterBottom>
              Enviar propuesta
            </Typography>
          </Container>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 14 }}>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        <TextField
          autoFocus
          margin="dense"
          id="description"
          label="Descripción de la propuesta"
          type="text"
          fullWidth
          name="description"
          multiline
          rows={5}
          value={formData.description}
          onChange={handleInputChange}
        />
        <Box sx={{ mt: 2, mb: 1 }}>
          {budget > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Presupuesto del cliente: <strong>USD$ {budget}</strong>
            </Typography>
          )}
          {budget > 0 && (
            <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
              <Chip
                label={`-10% · USD$ ${Math.round(budget * 0.9)}`}
                variant="outlined"
                onClick={() => setPrice(budget * 0.9)}
              />
              <Chip
                label={`Sugerido · USD$ ${budget}`}
                color="primary"
                onClick={() => setPrice(budget)}
              />
              <Chip
                label={`+10% · USD$ ${Math.round(budget * 1.1)}`}
                variant="outlined"
                onClick={() => setPrice(budget * 1.1)}
              />
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={() => handleStep(-priceRange.step)} size="small">
              <RemoveIcon />
            </IconButton>
            <Slider
              value={Number(formData.price) || 0}
              min={priceRange.min}
              max={priceRange.max}
              step={priceRange.step}
              onChange={handleSliderChange}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `USD$ ${value}`}
            />
            <IconButton onClick={() => handleStep(priceRange.step)} size="small">
              <AddIcon />
            </IconButton>
          </Box>
          <TextField
            margin="dense"
            id="price"
            label="Presupuesto"
            type="number"
            fullWidth
            name="price"
            value={formData.price}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: <InputAdornment position="start">USD$</InputAdornment>,
            }}
          />
        </Box>
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Plazo sugerido: <strong>{suggestedDays} día{suggestedDays !== 1 ? "s" : ""}</strong> (máximo {MAX_DAYS} días)
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
            <Chip
              label={`-20% · ${Math.min(MAX_DAYS, Math.max(1, Math.round(suggestedDays * 0.8)))} días`}
              variant="outlined"
              onClick={() => setDays(suggestedDays * 0.8)}
            />
            <Chip
              label={`Sugerido · ${suggestedDays} días`}
              color="primary"
              onClick={() => setDays(suggestedDays)}
            />
            <Chip
              label={`+20% · ${Math.min(MAX_DAYS, Math.round(suggestedDays * 1.2))} días`}
              variant="outlined"
              onClick={() => setDays(suggestedDays * 1.2)}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={() => handleDaysStep(-daysRange.step)} size="small">
              <RemoveIcon />
            </IconButton>
            <Slider
              value={Number(formData.timeToComplete) || 1}
              min={daysRange.min}
              max={daysRange.max}
              step={daysRange.step}
              onChange={handleDaysSliderChange}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value} días`}
            />
            <IconButton onClick={() => handleDaysStep(daysRange.step)} size="small">
              <AddIcon />
            </IconButton>
          </Box>
          <TextField
            margin="dense"
            id="timeToComplete"
            label="Tiempo estimado para completar"
            type="number"
            fullWidth
            name="timeToComplete"
            value={formData.timeToComplete}
            onChange={handleDaysInputChange}
            inputProps={{ min: 1, max: MAX_DAYS }}
            InputProps={{
              endAdornment: <InputAdornment position="end">días</InputAdornment>,
            }}
          />
        </Box>

        {publishSuccess ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography>La propuesta se ha enviado correctamente.</Typography>
            <Button onClick={handleRedirectHome} color="primary">
              Ir a Inicio
            </Button>
          </Box>
        ) : (
          <Button
            variant="contained"
            sx={{ mt: 3 }}
            fullWidth
            onClick={handleAddProposal}
            color="primary"
            disabled={!user || !project}
          >
            Publicar
          </Button>
        )}
      </Container>
    </Container>
  );
};

export default PublishProposal;